/**
 * Cliente Supabase para el backend compartido del PMIS (piloto).
 *
 * Se activa con `VITE_BACKEND=supabase`. Requiere en `.env.local`:
 *   VITE_SUPABASE_URL=https://<proyecto>.supabase.co
 *   VITE_SUPABASE_ANON_KEY=<anon public key>
 *
 * Patrón de almacenamiento — tabla única `pmis_entities`:
 *   entity      text   -- tipo de entidad ("afe", "so", "rd", ...)
 *   id          text   -- id de la entidad (UUID)
 *   tenant_id   text   -- aislamiento multi-tenant / multi-gerencia
 *   version     int    -- bloqueo optimista (espejo de payload.version)
 *   payload     jsonb  -- la entidad completa serializada (fuente de verdad)
 *   created_at  timestamptz
 *   updated_at  timestamptz
 *   PRIMARY KEY (entity, id)
 *
 * Este patrón replica el "single JSON column" del adaptador SharePoint:
 * permite evolucionar el esquema TS sin migraciones SQL y soporta los
 * arrays anidados de las entidades del PMIS. Para el piloto es suficiente;
 * a escala se agregan índices de expresión sobre `payload->>'campo'`.
 *
 * NOTA DE SEGURIDAD (piloto): se usa la `anon key` sin Supabase Auth. La
 * RLS del proyecto debe permitir lectura/escritura al rol anónimo (ver
 * `supabase/schema.sql`). Para producción: activar Supabase Auth y políticas
 * RLS por usuario/tenant.
 */

import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { obtenerTokenDatos } from "./dataToken";
import { DEPLOYMENT } from "@core/access/deployment";

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

let _client: SupabaseClient | null = null;

/** Devuelve el cliente Supabase (singleton). Lanza si faltan las env vars. */
export function supabase(): SupabaseClient {
  if (_client) return _client;
  if (!url || !anonKey) {
    throw new Error(
      "Backend supabase: faltan VITE_SUPABASE_URL y/o VITE_SUPABASE_ANON_KEY en .env.local.",
    );
  }
  _client = createClient(url, anonKey, {
    auth: { persistSession: false, autoRefreshToken: false },
    // Producción (Entra): cada request lleva el token del usuario como Bearer y
    // PostgREST aplica RLS sobre sus claims. Piloto: sin esto, manda la anon key.
    ...(DEPLOYMENT.dataAuth === "entra"
      ? { accessToken: async () => (await obtenerTokenDatos()) ?? anonKey }
      : {}),
  });
  return _client;
}

/** Nombre de la tabla única donde viven todas las entidades del PMIS. */
export const TABLE = "pmis_entities";

/** Error del backend Supabase con contexto. */
export class SupabaseRepoError extends Error {
  constructor(
    message: string,
    public readonly contexto: string,
  ) {
    super(`${contexto}: ${message}`);
    this.name = "SupabaseRepoError";
  }
}
